
/**
 * SequencePanel — right-side slide-over that visualises and lets the user
 * tweak the rule execution order.
 *
 * Cross-tier order is dictated by `{{out:Field}}` dependencies and CANNOT be
 * overridden here. Within a single tier, drag-and-drop reorders columns and
 * persists the new order into `profile.executionOrder`.
 *
 * The panel re-computes `analyzeDependencies` against the live profile on
 * every render so cycles and dangling refs update as the user edits rules
 * elsewhere.
 */

import { useMemo, useState } from "react";
import {
  analyzeDependencies,
  cycleWarnings,
  danglingRefWarnings,
  type DependencyAnalysis,
} from "@/lib/mapper/dependencies";
import type { Profile, Rule } from "@/lib/mapper/types";
import { cellModule } from "@/lib/mapper/modules/catalog";

type Props = {
  open: boolean;
  profile: Profile;
  onClose: () => void;
  onChangeProfile: (next: Profile) => void;
  onSelectColumn?: (column: string) => void;
};

type KindStyle = {
  label: string;
  cls: string;
};

function kindStyle(kind: Rule["kind"]): KindStyle {
  switch (kind) {
    case "source":
      return {
        label: "Source",
        cls: "bg-slate-100 dark:bg-surface-2 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-700",
      };
    case "static":
      return {
        label: "Static",
        cls: "bg-sky-100 text-sky-800 ring-1 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800",
      };
    case "template":
      return {
        label: "Template",
        cls: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800",
      };
    case "computed":
      return {
        label: "Computed",
        cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800",
      };
    case "dictionary":
      return {
        label: "Dictionary",
        cls: "bg-sky-100 text-sky-800 ring-1 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800",
      };
    case "dropbox-image":
      return {
        label: "Dropbox Image",
        cls: "bg-teal-100 text-teal-800 ring-1 ring-teal-200 dark:bg-teal-950/50 dark:text-teal-300 dark:ring-teal-800",
      };
    case "find-replace":
      return {
        label: "Find & Replace",
        cls: "bg-sky-100 text-sky-800 ring-1 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800",
      };
    case "expression":
      return {
        label: "Expression",
        cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800",
      };
    case "conditional":
      return {
        label: "Conditional",
        cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800",
      };
    case "ai":
      return {
        label: "AI",
        cls: "bg-violet-100 dark:bg-violet-950/50 text-violet-800 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-800",
      };
    default:
      // New / future module kinds self-describe via the registry — no per-kind
      // edit needed here.
      return {
        label: cellModule(kind)?.name ?? kind,
        cls: "bg-slate-100 dark:bg-surface-2 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-700",
      };
  }
}

export function SequencePanel({
  open,
  profile,
  onClose,
  onChangeProfile,
  onSelectColumn,
}: Props) {
  const analysis: DependencyAnalysis = useMemo(
    () => analyzeDependencies(profile),
    [profile],
  );
  const cycleMessages = useMemo(() => cycleWarnings(analysis), [analysis]);
  const danglingMessages = useMemo(
    () => danglingRefWarnings(analysis),
    [analysis],
  );

  // The dragged column + the tier it belongs to. Drops are constrained to the
  // same tier — cross-tier moves would violate dependency invariants.
  const [dragging, setDragging] = useState<{
    column: string;
    tierIdx: number;
  } | null>(null);

  // Which row the user is hovering during a drag (for an insertion line).
  const [dropTarget, setDropTarget] = useState<{
    tierIdx: number;
    column: string;
    edge: "before" | "after";
  } | null>(null);

  const cyclesByColumn = useMemo(() => {
    const set = new Set<string>();
    for (const cycle of analysis.cycles) {
      for (const c of cycle) set.add(c);
    }
    return set;
  }, [analysis.cycles]);

  function reorderWithinTier(
    tier: string[],
    movedColumn: string,
    targetColumn: string,
    edge: "before" | "after",
  ): string[] {
    const without = tier.filter((c) => c !== movedColumn);
    const targetIdx = without.indexOf(targetColumn);
    if (targetIdx === -1) return tier;
    const insertAt = edge === "before" ? targetIdx : targetIdx + 1;
    return [
      ...without.slice(0, insertAt),
      movedColumn,
      ...without.slice(insertAt),
    ];
  }

  /**
   * Persist a tier's new sibling order to `profile.executionOrder`.
   *
   * We keep the user's *full* preference list (across every tier) intact so
   * other tiers don't get reset. The new tier order is spliced in by removing
   * the columns we're about to re-rank and re-inserting them at the position
   * of the first one removed.
   */
  function commitTierOrder(newTier: string[]) {
    const previousOrder = profile.executionOrder ?? [];
    const tierSet = new Set(newTier);

    // Find the first index in previousOrder that belonged to this tier — we'll
    // anchor the new tier slice there. If none, append.
    let anchor = -1;
    for (let i = 0; i < previousOrder.length; i++) {
      if (tierSet.has(previousOrder[i])) {
        anchor = i;
        break;
      }
    }

    const stripped = previousOrder.filter((c) => !tierSet.has(c));
    const next =
      anchor === -1
        ? [...stripped, ...newTier]
        : [...stripped.slice(0, anchor), ...newTier, ...stripped.slice(anchor)];

    onChangeProfile({ ...profile, executionOrder: next });
  }

  function handleDrop(targetTierIdx: number, targetColumn: string) {
    if (!dragging) return;
    if (dragging.tierIdx !== targetTierIdx) {
      // Cross-tier drops are not allowed.
      setDragging(null);
      setDropTarget(null);
      return;
    }
    const tier = analysis.tiers[targetTierIdx];
    if (!tier) return;
    const edge =
      dropTarget && dropTarget.column === targetColumn
        ? dropTarget.edge
        : "after";
    if (dragging.column === targetColumn) {
      setDragging(null);
      setDropTarget(null);
      return;
    }
    const newTier = reorderWithinTier(
      tier,
      dragging.column,
      targetColumn,
      edge,
    );
    commitTierOrder(newTier);
    setDragging(null);
    setDropTarget(null);
  }

  return (
    <>
      {/* Backdrop — semi-transparent. Click to close. */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={[
          "fixed inset-0 z-40 bg-slate-900/20 transition-opacity",
          open
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0",
        ].join(" ")}
      />

      {/* Slide-over */}
      <aside
        role="dialog"
        aria-label="Run order"
        className={[
          "fixed right-0 top-0 z-50 flex h-full w-[380px] flex-col border-l border-slate-200 dark:border-border bg-white dark:bg-surface shadow-xl transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 dark:border-border px-4 py-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Template
            </div>
            <div className="text-[14px] font-semibold text-slate-900 dark:text-slate-100">
              Run order
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close run order"
            className="rounded p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-surface-2 hover:text-slate-900 dark:hover:text-slate-100"
          >
            ✕
          </button>
        </div>

        {/* Intro */}
        <div className="shrink-0 border-b border-slate-100 dark:border-border bg-slate-50 dark:bg-background px-4 py-2.5 text-[11.5px] leading-snug text-slate-600 dark:text-slate-400">
          Rules run in dependency order. Within an independent tier, drag to
          reorder. Cross-tier order is fixed by{" "}
          <code className="rounded bg-white dark:bg-surface px-1 font-mono text-[10.5px]">
            workbook references
          </code>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {analysis.cycles.length > 0 && (
            <div className="mb-3 rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-2.5 text-[11.5px] text-red-900">
              <div className="font-semibold">⚠ Rule dependency loop</div>
              <ul className="mt-1.5 space-y-1.5">
                {analysis.cycles.map((cycle, i) => (
                  <li key={i}>
                    <div className="text-[11px] leading-snug">
                      {cycleMessages[i]}
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] text-red-700 dark:text-red-300">
                      {cycle.join(" → ")}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.danglingRefs.length > 0 && (
            <div className="mb-3 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-2.5 text-[11.5px] text-amber-900 dark:text-amber-200">
              <div className="font-semibold">⚠ Dangling output references</div>
              <ul className="mt-1.5 space-y-1">
                {danglingMessages.map((message, i) => (
                  <li key={i} className="text-[11px] leading-snug">
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.tiers.length === 0 && (
            <div className="rounded border border-dashed border-slate-300 dark:border-border bg-slate-50 dark:bg-background px-4 py-8 text-center text-[12px] text-slate-500 dark:text-slate-400">
              No rules yet. Add a rule from the sidebar to see it here.
            </div>
          )}

          {analysis.tiers.map((tier, tierIdx) => (
            <div key={tierIdx} className="mb-4 last:mb-0">
              <div className="mb-1 flex items-baseline justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Tier {tierIdx + 1}
                </div>
                {tierIdx > 0 && (
                  <div className="text-[10px] text-slate-400 dark:text-slate-500">
                    runs after Tier {tierIdx} completes
                  </div>
                )}
              </div>
              <ul className="space-y-1 rounded border border-slate-200 dark:border-border bg-white dark:bg-surface p-1">
                {tier.map((column) => {
                  const rule = profile.fields[column];
                  const ks = rule ? kindStyle(rule.kind) : null;
                  const deps = analysis.depsByColumn[column] ?? [];
                  const isDragging =
                    dragging?.column === column &&
                    dragging?.tierIdx === tierIdx;
                  const showInsertionAbove =
                    dropTarget?.tierIdx === tierIdx &&
                    dropTarget?.column === column &&
                    dropTarget?.edge === "before" &&
                    dragging?.tierIdx === tierIdx &&
                    dragging?.column !== column;
                  const showInsertionBelow =
                    dropTarget?.tierIdx === tierIdx &&
                    dropTarget?.column === column &&
                    dropTarget?.edge === "after" &&
                    dragging?.tierIdx === tierIdx &&
                    dragging?.column !== column;
                  const inCycle = cyclesByColumn.has(column);
                  return (
                    <li
                      key={column}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", column);
                        setDragging({ column, tierIdx });
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropTarget(null);
                      }}
                      onDragOver={(e) => {
                        if (!dragging || dragging.tierIdx !== tierIdx) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        const rect = e.currentTarget.getBoundingClientRect();
                        const isBefore = e.clientY - rect.top < rect.height / 2;
                        setDropTarget({
                          tierIdx,
                          column,
                          edge: isBefore ? "before" : "after",
                        });
                      }}
                      onDragLeave={() => {
                        // We don't clear dropTarget here because dragOver fires
                        // continuously and the next sibling's dragOver will
                        // overwrite it. Clearing would cause flicker.
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleDrop(tierIdx, column);
                      }}
                      className={[
                        "group relative rounded border px-2 py-1.5 transition",
                        isDragging
                          ? "border-violet-300 bg-violet-50 dark:bg-violet-950/40 opacity-50"
                          : inCycle
                            ? "border-red-200 dark:border-red-900/60 bg-red-50/40"
                            : "border-slate-200 dark:border-border bg-white dark:bg-surface hover:border-slate-300 dark:hover:border-slate-600",
                      ].join(" ")}
                    >
                      {showInsertionAbove && (
                        <div className="absolute inset-x-1 -top-px h-0.5 rounded bg-violet-500" />
                      )}
                      {showInsertionBelow && (
                        <div className="absolute inset-x-1 -bottom-px h-0.5 rounded bg-violet-500" />
                      )}
                      <div className="flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="shrink-0 cursor-grab select-none text-[12px] text-slate-300 dark:text-slate-600 transition group-hover:text-slate-500"
                          title="Drag to reorder within this tier"
                        >
                          ⋮⋮
                        </span>
                        <button
                          type="button"
                          onClick={() => onSelectColumn?.(column)}
                          className="min-w-0 flex-1 truncate text-left font-mono text-[11.5px] text-slate-900 dark:text-slate-100 hover:underline"
                          title={column}
                        >
                          {column}
                        </button>
                        {ks && (
                          <span
                            className={[
                              "shrink-0 rounded px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide",
                              ks.cls,
                            ].join(" ")}
                          >
                            {ks.label}
                          </span>
                        )}
                      </div>
                      {deps.length > 0 && (
                        <div className="mt-0.5 pl-5 text-[10px] text-slate-500 dark:text-slate-400">
                          ↑ depends on{" "}
                          {deps.map((d, i) => (
                            <span key={d}>
                              <button
                                type="button"
                                onClick={() => onSelectColumn?.(d)}
                                className="font-mono text-slate-600 dark:text-slate-400 hover:text-violet-700 hover:underline"
                              >
                                {d}
                              </button>
                              {i < deps.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      {inCycle && (
                        <div className="mt-0.5 pl-5 text-[10px] font-semibold text-red-700 dark:text-red-300">
                          ⚠ Part of cycle
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
